import { useEffect, useRef, useState } from "react";
import { useColorTheme, type Theme, type ThemeBgColor } from "contexts/color-theme";
import { useHeaderHeight } from "contexts/header-height";

type ColorThemeWrapperProps = {
  theme?: Theme;
  bgColor?: ThemeBgColor;
};

/**
 * Hook that toggles `ColorTheme` theme and background color for the scope of an element.
 * Only use this hook with full width sections of the page (ex. sections, plates, footer, etc).
 *
 * IMPORTANT: Margins are not considered when calculating the intersection of a component.
 * If your component isn't intersecting correctly, you may need to use `py` instead of `my`.
 */
export function useColorThemeToggle({ theme = "light", bgColor = "white" }: ColorThemeWrapperProps) {
  const [rootMarginBottom, setRootMarginBottom] = useState(0);
  const { headerHeight } = useHeaderHeight();
  const { setTheme, setBgColor } = useColorTheme();
  const scope = useRef(null);

  useEffect(() => {
    const calculateHeight = () => {
      const height = window.innerHeight - headerHeight;
      setRootMarginBottom(height);
    };

    calculateHeight();

    const resizeObserver = new ResizeObserver(() => {
      calculateHeight();
    });
    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, [headerHeight]);

  useEffect(() => {
    const scopeElement = scope.current;

    const updateTheme = () => {
      setTheme(theme);
      setBgColor(bgColor);
    };

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const sectionTop = entry.boundingClientRect.top;
          const sectionBottom = entry.boundingClientRect.bottom;

          if (sectionTop < headerHeight && sectionBottom > headerHeight) {
            updateTheme();
          }
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: 0,
      rootMargin: `-${headerHeight}px 0px -${rootMarginBottom}px 0px`, // Intersect only at the top 92 pixels
    });

    if (scopeElement) {
      observer.observe(scopeElement);
    }

    return () => {
      if (scopeElement) {
        observer.unobserve(scopeElement);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootMarginBottom, theme, bgColor]);

  return scope;
}
